// Menu streaks (pure — no DB). Derived from the archived (finished) menu days
// via the same daily series the history charts use, so "on target" means
// exactly what the energy chart's green marker means.
//
// Two streaks, each `{ current, best }` (in days):
//   - tracked  — consecutive days with a finished (archived) menu;
//   - onTarget — consecutive days whose energy eaten was at/below that day's
//                upper limit (goal + max(over %, bonus) + morning surplus).
//                Under-eating still counts; a day with no stored goal (or
//                unknown energy) can't be scored, so it BREAKS the streak.
//
// Grace: TODAY and any day still on the menu awaiting "finish" (a `pending`
// date) neither extend nor break a streak — the day isn't over/recorded yet. A
// past day that's neither archived nor pending was skipped → breaks it.

import { dailySeries } from './nutrition-history.js';
import { archivedMenuDate, formatLocalDate } from './menu.js';

/** Only show a streak once it's at least this long. */
export const MIN_STREAK_DAYS = 3;

/** Classify one day for each streak: 'hit' | 'skip' | 'miss'. */
function classify(point, graced) {
  if (graced) return { tracked: 'skip', onTarget: 'skip' };
  if (!point.archiveCount) return { tracked: 'miss', onTarget: 'miss' };
  const green = point.kj !== null && point.upperKj !== null && point.kj <= point.upperKj;
  return { tracked: 'hit', onTarget: green ? 'hit' : 'miss' };
}

/**
 * `menuStreaks(archives, { today, pendingDates })` →
 * `{ tracked: { current, best }, onTarget: { current, best } }`.
 * `today` is a Date or `YYYY-MM-DD`; `pendingDates` are the `YYYY-MM-DD` dates
 * of live (unfinished) menu days. A pending date is graced even if it already
 * has an archive (its final result isn't known yet).
 */
export function menuStreaks(archives, { today = new Date(), pendingDates = [] } = {}) {
  const todayStr = typeof today === 'string' ? today : formatLocalDate(today);
  const pending = new Set(pendingDates);
  const empty = { tracked: { current: 0, best: 0 }, onTarget: { current: 0, best: 0 } };

  const dates = (archives ?? [])
    .map(archivedMenuDate)
    .filter((d) => d && d <= todayStr)
    .sort();
  if (dates.length === 0) return empty;

  const runs = { tracked: { run: 0, best: 0 }, onTarget: { run: 0, best: 0 } };
  for (const point of dailySeries(archives, { from: dates[0], to: todayStr })) {
    const graced = pending.has(point.date) || (point.date === todayStr && !point.archiveCount);
    const kinds = classify(point, graced);
    for (const key of Object.keys(runs)) {
      const r = runs[key];
      if (kinds[key] === 'hit') r.run += 1;
      else if (kinds[key] === 'miss') r.run = 0;
      r.best = Math.max(r.best, r.run);
    }
  }
  return {
    tracked: { current: runs.tracked.run, best: runs.tracked.best },
    onTarget: { current: runs.onTarget.run, best: runs.onTarget.best },
  };
}
